import { useCallback, useEffect, useMemo, useState } from "react";
import { PortalRentals, RENTAL_STATUS_LABELS } from "../../components/portal/PortalRentals";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { CheckCircle2, LogOut } from "lucide-react";
import { api, errMsg, fmtDate } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { StatusBadge, ActionButton, EmptyState, DataTable, Td, SelectInput, TextInput } from "../../components/common";

const EMPTY_FILTER = { query: "", status: "" };
const SWD_COLUMNS = ["Ticket", "Job Status", "Item", "Qty", "Pickup", "Destination", "Submitted", "Approval", ""];

export default function RequestorPortal() {
  const { user, tenant, logout } = useAuth();
  const navigate = useNavigate();
  const [tickets, setTickets] = useState([]);
  const [busyId, setBusyId] = useState(null);
  const [rentals, setRentals] = useState([]);
  const [tab, setTab] = useState("swd");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const hasSwd = !!tenant?.enabled_modules?.includes("swd");
  const hasRental = !!tenant?.enabled_modules?.includes("fractank");
  const [filters, setFilters] = useState({ swd: EMPTY_FILTER, rental: EMPTY_FILTER });
  const [debouncedQuery, setDebouncedQuery] = useState("");
  const [filterOptions, setFilterOptions] = useState({ swd: [], rental: [] });
  const selectedTab = hasSwd ? tab : "rental";
  const currentFilter = filters[selectedTab];
  const statusOptions = useMemo(() => [{ value: "", label: "All statuses" }, ...filterOptions[selectedTab].map((value) => ({ value, label: selectedTab === "rental" ? RENTAL_STATUS_LABELS[value] || value : value }))], [filterOptions, selectedTab]);
  useEffect(() => { const timer = setTimeout(() => setDebouncedQuery(currentFilter.query.trim()), 250); return () => clearTimeout(timer); }, [currentFilter.query]);

  const load = useCallback(async (signal) => {
    setLoading(true); setError("");
    const params = { q: debouncedQuery, status: currentFilter.status };
    try {
      const [swd, rental, facets] = await Promise.all([
        hasSwd ? api.get("/portal/tickets", { params: selectedTab === "swd" ? params : {}, signal }) : Promise.resolve({ data: [] }),
        hasRental ? api.get("/portal/rentals", { params: selectedTab === "rental" ? params : {}, signal }) : Promise.resolve({ data: [] }),
        api.get("/portal/filters", { signal }),
      ]);
      if (!signal?.aborted) { setTickets(swd.data); setRentals(rental.data); setFilterOptions(facets.data); }
    } catch (e) { if (!signal?.aborted) { setTickets([]); setRentals([]); setError(errMsg(e)); } }
    finally { if (!signal?.aborted) setLoading(false); }
  }, [hasSwd, hasRental, selectedTab, debouncedQuery, currentFilter.status]);
  useEffect(() => { const controller = new AbortController(); load(controller.signal); return () => controller.abort(); }, [load]);

  const approve = async (t) => {
    setBusyId(t.id);
    try {
      await api.post(`/portal/tickets/${t.id}/approve`);
      toast.success(`${t.ticket_number} approved`);
      load();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusyId(null);
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  return (
    <div className="min-h-screen bg-background tactical-grid" data-testid="portal-page">
      <header className="sticky top-0 z-30 bg-background border-b border-slate-800 px-4 sm:px-6 py-3 flex flex-wrap gap-3 items-center justify-between">
        <div className="flex items-center gap-3">
          <img src="/logo.png" alt="Well Fleet Logic" className="w-9 h-9 rounded bg-white p-0.5 object-contain border border-slate-800" data-testid="brand-logo" />
          <div>
            <p className="font-heading text-lg font-bold tracking-wide text-slate-50 leading-none">WELL FLEET LOGIC</p>
            <p className="text-[10px] font-mono uppercase tracking-[0.3em] text-amber-500/80">Requester Portal</p>
          </div>
        </div>
        <div className="flex items-center gap-4">
          <div className="text-right">
            <p className="text-sm font-medium text-slate-200" data-testid="portal-user-name">{user?.name}</p>
            <p className="text-[10px] font-mono uppercase tracking-widest text-slate-500">{tenant?.name}</p>
          </div>
          <button
            data-testid="portal-logout-button"
            onClick={handleLogout}
            className="p-2 rounded-md text-slate-500 hover:text-red-400 hover:bg-red-500/10 transition-colors duration-200"
            title="Sign out"
          >
            <LogOut size={16} />
          </button>
        </div>
      </header>

      <main className="max-w-6xl mx-auto p-4 sm:p-6 lg:p-8 min-w-0">
        <div className="mb-6">
          <p data-testid="portal-company-name" className="text-xs font-mono uppercase tracking-[0.2em] text-amber-500/80 mb-1 break-words">{user?.customer_name}</p>
          <h1 className="font-heading text-4xl sm:text-5xl font-bold tracking-tight text-slate-50">Your Company Tickets</h1>
          <p data-testid="portal-scope-help" className="text-sm text-slate-400 mt-2">All Rental and SWD tickets for your assigned company, including requests made by other contacts. Review and approve SWD tickets; view Rental tickets and update well stages for your assigned company.</p>
        </div>
        <div className="flex flex-wrap gap-2 mb-5" data-testid="portal-ticket-tabs">
          {hasSwd && <ActionButton testId="portal-swd-tab" variant={selectedTab === "swd" ? "primary" : "secondary"} onClick={() => setTab("swd")} aria-pressed={selectedTab === "swd"}>SWD ({tickets.length})</ActionButton>}
          {hasRental && <ActionButton testId="portal-rental-tab" variant={selectedTab === "rental" ? "primary" : "secondary"} onClick={() => setTab("rental")} aria-pressed={selectedTab === "rental"}>Rental ({rentals.length})</ActionButton>}
          <ActionButton testId="portal-refresh-button" variant="secondary" disabled={loading} onClick={() => load()}>Refresh</ActionButton>
        </div>
        <div className="flex flex-col sm:flex-row gap-3 mb-3" data-testid="portal-search-controls">
          <TextInput testId="portal-ticket-search" aria-label="Search company tickets" maxLength={200} value={currentFilter.query} onChange={(e) => setFilters((prev) => ({ ...prev, [selectedTab]: { ...prev[selectedTab], query: e.target.value } }))} placeholder={selectedTab === "rental" ? "Search rental ticket, location, equipment or requester" : "Search SWD ticket, item, pickup or destination"} />
          <SelectInput testId="portal-status-filter" aria-label="Filter ticket status" className="sm:!w-64" value={currentFilter.status} onChange={(e) => setFilters((prev) => ({ ...prev, [selectedTab]: { ...prev[selectedTab], status: e.target.value } }))} options={statusOptions} />
          <ActionButton testId="portal-clear-filters" variant="secondary" onClick={() => setFilters((prev) => ({ ...prev, [selectedTab]: EMPTY_FILTER }))}>Clear Filters</ActionButton>
        </div>
        <p data-testid="portal-results-count" className="text-xs text-slate-500 mb-4">{loading ? "Searching…" : `${selectedTab === "rental" ? rentals.length : tickets.length} matching ${selectedTab === "rental" ? "Rental" : "SWD"} tickets`}</p>
        {error ? <div role="alert" data-testid="portal-load-error" className="text-sm text-red-600">{error}<ActionButton testId="portal-retry-button" variant="secondary" onClick={() => load()} className="ml-2">Try Again</ActionButton></div> : loading ? <p data-testid="portal-loading" role="status">Loading company tickets…</p> : !hasSwd && !hasRental ? <EmptyState message="No ticket modules are enabled for this account" testId="portal-no-modules" /> : selectedTab === "rental" ? <PortalRentals rentals={rentals} onStageSaved={load} /> : tickets.length === 0 ? (
          <EmptyState message="No SWD tickets for your company" testId="portal-tickets-empty" />
        ) : (
          <DataTable
            testId="portal-tickets-table"
            columns={SWD_COLUMNS}
            rows={tickets}
            renderRow={(t) => (
              <tr key={t.id} className="hover:bg-slate-800/30 transition-colors duration-150" data-testid={`portal-ticket-row-${t.id}`}>
                <Td className="font-mono text-amber-400">{t.ticket_number}</Td>
                <Td>{t.job_status || "—"}</Td>
                <Td>{t.item || "—"}</Td>
                <Td className="font-mono">{t.qty ?? "—"}</Td>
                <Td className="max-w-[140px] truncate">{t.pickup_location || "—"}</Td>
                <Td className="max-w-[140px] truncate">{t.destination || "—"}</Td>
                <Td className="text-xs text-slate-500">{fmtDate(t.submitted)}</Td>
                <Td>
                  <StatusBadge status={(t.approval_status || "Not Approved") === "Approved" ? "approved" : "submitted"} />
                  {t.approved_by && <p className="text-[10px] text-slate-500 mt-1 font-mono">{t.approved_by}</p>}
                </Td>
                <Td>
                  {t.approval_status !== "Approved" && (
                    <ActionButton testId={`portal-approve-${t.id}`} onClick={() => approve(t)} disabled={busyId === t.id} className="!px-3 !py-1.5 text-xs">
                      <CheckCircle2 size={14} /> Approve
                    </ActionButton>
                  )}
                </Td>
              </tr>
            )}
          />
        )}
      </main>
    </div>
  );
}
